import { Fragment, useMemo, useCallback, useEffect, useRef, useState, memo, ReactNode } from "react";
import cx from "classnames";
import { CellMouseEvent, ColumnWidths, DataGridHandle } from "react-data-grid";
import { columnEditorConfig } from "./editableFields";
import type { BuildingSchema, BuildingSchemaColumn } from "../../../ts-types/mapsTypes";
import { EditableCellRouter } from "./editors/EditableCellRouter";
import { TopBuildingsListColumnHeader, type TopBuildingsListColumnBulkEdit } from "./TopBuildingsListColumnHeader";

import { Button, DataGrid, Flex, Icon, Input, Link } from "@repo/ping-design";

import { formatCurrency, formatWithCommas } from "../../../utils/formatters";
import { MISSING_VALUE } from "../../Constants";
import { GEOCODING_CONFIDENCE_MAPPING } from "../../../utils/constants";
import { TopBuildingsListConfigureModal, ColumnConfigItem } from "./TopBuildingsListConfigureModal";
import { COLUMN_WIDTH } from "./constants";
import { useTopBuildingsListConfig, type ConfigurableColumn } from "./useTopBuildingsListConfig";

import "./TopBuildingsList.scss";

export type TopBuildingsListRow = {
  id: string;
  lat?: number;
  lng?: number;
  // The building's values, by building schema column key.
  values: Record<string, unknown>;
};

export type TopBuildingsListProps = {
  topBuildings: any[];
  // The grid's columns for this viewer, from the policy overview; null/undefined until it loads.
  buildingSchema?: BuildingSchema | null;
  // The visible column keys (sorted), i.e. the fields the buildings should be fetched with.
  onVisibleColumnsChange?: (keys: string[]) => void;
  // Columns that require a layer show only while one is set.
  layerLimit?: number | null;
  focusedBuildingIds?: string[] | null;
  canEdit?: boolean;
  // Cells are keyed `${buildingId}:${columnKey}`.
  lockedCellKeys?: ReadonlySet<string>;
  onColumnBulkEdit?: (attr: string, value: string | number | null) => void;
  bulkEditDisabled?: boolean;
  bulkEditRowCount?: number;
  onUpdateBuildingField?: (buildingId: string, attr: string, value: string | number | null) => Promise<boolean>;
  onBuildingClick?: (building: TopBuildingsListRow) => void;
  onBuildingDoubleClick?: (building: TopBuildingsListRow) => void;
  onBuildingContextMenu?: (building: TopBuildingsListRow, event: CellMouseEvent) => void;
  onSelectedCellChange?: (building: TopBuildingsListRow | null, rowIdx: number) => void;
  className?: string;
  // When set, users can reorder/hide columns via the configure modal; the
  // configuration is persisted in localStorage under this key.
  storageKey?: string;
  onSearchChange?: (search: string) => void;
  totalCount?: number;
  resetKey?: string;
  hasMore?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  onLoadMore?: () => unknown;
  onRetry?: () => unknown;
  onCollapse?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
};

const SEARCH_MAX_LENGTH = 250;
const NO_SCHEMA_COLUMNS: BuildingSchemaColumn[] = [];

const matchLevelCell = (matchLevel: string) => (
  <Flex align="center" gap="2">
    <Icon iconName={matchLevel === "point" ? "AccuracyPointPing" : "AccuracyNonPointPing"} />
    {GEOCODING_CONFIDENCE_MAPPING?.[matchLevel as keyof typeof GEOCODING_CONFIDENCE_MAPPING]}
  </Flex>
);

// An Excel width counts characters of "0" in the sheet's font, which is what `ch` measures in the grid's; the
// cell's side padding comes on top, where Excel's is built in.
const excelColumnWidth = (width: number) => `calc(${width}ch + 2 * var(--ping-spacing-4))`;

// Link values can come from vendors, so only web URLs become links.
const linkCell = (url: string) =>
  /^https?:\/\//i.test(url) ? (
    <Link href={url} target="_blank" variant="brand">
      <Flex align="center" gap="1">
        <Icon iconName="ExternalLink" size={14} />
        Open
      </Flex>
    </Link>
  ) : (
    url
  );

const PERCENT = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 2 });

const formatValue = (column: BuildingSchemaColumn, value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "") return MISSING_VALUE;
  // One value per item, e.g. per layer covering a peril: each formatted like a single value.
  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <Fragment key={index}>
        {index > 0 && " / "}
        {formatValue(column, item)}
      </Fragment>
    ));
  }
  if (column.display === "geocode_match_level") return matchLevelCell(String(value));
  if (column.display === "link") return linkCell(String(value));
  if (column.display === "percent") return PERCENT.format(Number(value));
  if (column.options) {
    return column.options.find((option) => String(option.value) === String(value))?.label ?? String(value);
  }
  switch (column.datatype) {
    case "CURRENCY":
      return formatCurrency(value);
    case "NUMBER":
      return formatWithCommas(value as number);
    case "FLOAT":
    case "PERCENTAGE":
      return formatWithCommas(value as number, false);
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

export const TopBuildingsList = memo(
  ({
    topBuildings,
    buildingSchema,
    onVisibleColumnsChange,
    layerLimit,
    focusedBuildingIds,
    canEdit = false,
    lockedCellKeys,
    onColumnBulkEdit,
    bulkEditDisabled = false,
    bulkEditRowCount = 0,
    onUpdateBuildingField,
    onBuildingClick,
    onBuildingDoubleClick,
    onBuildingContextMenu,
    onSelectedCellChange,
    className,
    storageKey,
    onSearchChange,
    totalCount,
    resetKey,
    hasMore = false,
    isLoading = false,
    hasError = false,
    onLoadMore,
    onRetry,
    onCollapse,
    isFullscreen,
    onToggleFullscreen,
  }: TopBuildingsListProps) => {
    const [search, setSearch] = useState("");
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

    const schemaColumns = buildingSchema?.columns ?? NO_SCHEMA_COLUMNS;
    const schemaColumnsByKey = useMemo(() => new Map(schemaColumns.map((col) => [col.key, col])), [schemaColumns]);
    const gridColumns = useMemo<ConfigurableColumn[]>(
      () =>
        schemaColumns.map((col) => ({
          key: col.key,
          name: col.caption,
          resizable: true,
          width: col.width === null ? COLUMN_WIDTH : excelColumnWidth(col.width),
          groups: col.groups ?? undefined,
        })),
      [schemaColumns],
    );
    const defaultHiddenKeys = useMemo(
      () => schemaColumns.filter((col) => !col.default_visible).map((col) => col.key),
      [schemaColumns],
    );
    const layoutKey = storageKey ?? "top-buildings-list-columns";

    const { configuredBaseColumns, allColumnDefs, applyConfig, resetToDefault, isCustomized } =
      useTopBuildingsListConfig(
        buildingSchema ? `${layoutKey}:${buildingSchema.layout_key}` : layoutKey,
        gridColumns,
        defaultHiddenKeys,
      );
    // A column that requires something not in effect (e.g. a layer) is left out until it is.
    const shownColumns = useMemo(
      () =>
        configuredBaseColumns.filter((col) => {
          const requires = schemaColumnsByKey.get(col.key)?.requires;
          return !requires || (requires === "layer" && !!layerLimit);
        }),
      [configuredBaseColumns, schemaColumnsByKey, layerLimit],
    );

    // Keyed by layout too, so switching schemas re-reports even an identical column set.
    const visibleColumnsSignature = buildingSchema
      ? JSON.stringify([buildingSchema.layout_key, shownColumns.map((col) => col.key).sort()])
      : null;
    useEffect(() => {
      if (visibleColumnsSignature !== null) onVisibleColumnsChange?.(JSON.parse(visibleColumnsSignature)[1]);
    }, [visibleColumnsSignature, onVisibleColumnsChange]);
    const gridRef = useRef<DataGridHandle | null>(null);
    const loadingMore = useRef(false);
    const setGridRef = useCallback((grid: DataGridHandle | null) => {
      gridRef.current = grid;
    }, []);

    useEffect(() => {
      const timer = setTimeout(() => onSearchChange?.(search.trim()), 300);
      return () => clearTimeout(timer);
    }, [search, onSearchChange]);

    useEffect(() => {
      const element = gridRef.current?.element;
      if (element) element.scrollTop = 0;
    }, [resetKey]);

    const loadMore = useCallback(() => {
      const element = gridRef.current?.element;
      if (!element || !hasMore || isLoading || hasError || loadingMore.current || !onLoadMore) return;
      const { scrollTop, scrollHeight, clientHeight } = element;
      if (clientHeight <= 0 || scrollHeight - scrollTop - clientHeight > 200) return;
      loadingMore.current = true;
      Promise.resolve(onLoadMore()).finally(() => {
        loadingMore.current = false;
      });
    }, [hasMore, isLoading, hasError, onLoadMore]);

    useEffect(() => {
      loadMore();
      const element = gridRef.current?.element;
      if (!element || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(loadMore);
      observer.observe(element);
      return () => observer.disconnect();
    }, [loadMore, topBuildings.length]);

    // Only columns in the saved config can be hidden; the rest are added around it.
    const hideableColumnKeys = useMemo(
      () => new Set(storageKey ? allColumnDefs.map((c) => c.key) : []),
      [storageKey, allColumnDefs],
    );
    const hideColumn = useCallback(
      (key: string) => {
        applyConfig({
          orderedKeys: allColumnDefs.map((c) => c.key),
          hiddenKeys: [...allColumnDefs.filter((c) => !c.isVisible).map((c) => c.key), key],
        });
      },
      [allColumnDefs, applyConfig],
    );

    const breakdownColumns = useMemo(() => {
      return shownColumns.map((col) => {
        const schemaColumn = schemaColumnsByKey.get(col.key);
        const onHide = hideableColumnKeys.has(col.key) ? () => hideColumn(col.key) : undefined;
        const title = schemaColumn?.group ?? undefined;
        const header = (bulkEdit?: TopBuildingsListColumnBulkEdit) =>
          (bulkEdit || onHide || title) && {
            renderHeaderCell: () => (
              <TopBuildingsListColumnHeader name={col.name} title={title} bulkEdit={bulkEdit} onHide={onHide} />
            ),
          };
        if (!schemaColumn) return { ...col, ...header() };
        const display = (row: TopBuildingsListRow) => formatValue(schemaColumn, row.values[col.key]);
        const fieldConfig = canEdit ? columnEditorConfig(schemaColumn) : undefined;
        if (!fieldConfig) return { ...col, ...header(), renderCell: (props: any) => display(props.row) };
        const isDropdown = fieldConfig.type === "dropdown";
        const cellClass = isDropdown ? "TopBuildingsList__EditableCellDropdown" : "TopBuildingsList__EditableCell";
        const isLocked = (rowId: string) => lockedCellKeys?.has(`${rowId}:${col.key}`) ?? false;
        const canBulkEdit = onColumnBulkEdit && (fieldConfig.type === "dropdown" || fieldConfig.type === "money");
        return {
          ...col,
          ...header(
            canBulkEdit
              ? {
                  rowKey: col.key,
                  config: fieldConfig,
                  rowCount: bulkEditRowCount,
                  disabled: bulkEditDisabled,
                  onApply: onColumnBulkEdit,
                }
              : undefined,
          ),
          editable: (row: any) => !isLocked(row.id),
          renderCell: (props: any) => (
            <div
              className={cx(cellClass, {
                "TopBuildingsList__EditableCell--Pending": isLocked(props.row.id),
              })}
            >
              {display(props.row)}
            </div>
          ),
          renderEditCell: (props: any) => (
            <EditableCellRouter
              {...props}
              config={fieldConfig}
              onSave={(value) =>
                onUpdateBuildingField ? onUpdateBuildingField(props.row.id, col.key, value) : Promise.resolve(false)
              }
            />
          ),
        };
      });
    }, [
      shownColumns,
      schemaColumnsByKey,
      canEdit,
      lockedCellKeys,
      onColumnBulkEdit,
      bulkEditDisabled,
      bulkEditRowCount,
      onUpdateBuildingField,
      hideableColumnKeys,
      hideColumn,
    ]);

    const rows = useMemo<TopBuildingsListRow[]>(
      () =>
        (topBuildings || []).map((building) => ({
          id: building?.item_key,
          lat: building?.latitude,
          lng: building?.longitude,
          values: building,
        })),
      [topBuildings],
    );

    const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => new Map());
    // Re-fit columns for a new set of buildings, not when edits or paging change rows: re-measuring briefly
    // collapses every column, which clamps the horizontal scroll back to the left.
    useEffect(() => {
      setColumnWidths((prev) =>
        [...prev.values()].some((w) => w.type === "measured")
          ? new Map([...prev].filter(([, w]) => w.type === "resized"))
          : prev,
      );
    }, [resetKey]);

    const handleCellClick = useCallback(
      (args: any) => {
        onBuildingClick?.(args.row);
        // Enter edit mode on single click if the column has an editor.
        if (args.column?.renderEditCell && !lockedCellKeys?.has(`${args.row.id}:${args.column.key}`)) {
          gridRef.current?.selectCell({ rowIdx: args.rowIdx, idx: args.column.idx }, { enableEditor: true });
        }
      },
      [onBuildingClick, lockedCellKeys],
    );

    const handleCellDoubleClick = useCallback(
      (args: any) => {
        onBuildingDoubleClick?.(args.row);
      },
      [onBuildingDoubleClick],
    );

    const handleSelectedCellChange = useCallback(
      (args: any) => {
        if (args?.rowIdx >= 0) {
          onSelectedCellChange?.(args.row, args.rowIdx);
        }
      },
      [onSelectedCellChange],
    );

    const handleCellContextMenu = useCallback(
      (args: any, event: CellMouseEvent) => {
        event.preventDefault();
        onBuildingContextMenu?.(args.row, event);
      },
      [onBuildingContextMenu],
    );

    const handleSaveConfig = useCallback(
      (items: ColumnConfigItem[]) => {
        applyConfig({
          orderedKeys: items.map((i) => i.key),
          hiddenKeys: items.filter((i) => !i.isVisible).map((i) => i.key),
        });
        setIsConfigModalOpen(false);
      },
      [applyConfig],
    );

    const handleResetConfig = useCallback(() => {
      resetToDefault();
      setIsConfigModalOpen(false);
    }, [resetToDefault]);

    return (
      <div className="TopBuildingsListContainer">
        <div className="TopBuildingsListContainer__TitleRow">
          <div className="TopBuildingsListContainer__Title">
            <span className="TopBuildingsListContainer__Title__Heading">Top buildings by TIV</span>
            <span className="TopBuildingsListContainer__Title__Count" role="status">
              (Showing {formatWithCommas(rows.length)}
              {totalCount !== undefined ? ` of ${formatWithCommas(totalCount)}` : ""} filtered locations)
            </span>
          </div>
          <div className="TopBuildingsListContainer__Actions">
            {onSearchChange && (
              <>
                <label className="TopBuildingsListContainer__SearchLabel" htmlFor="top-buildings-search">
                  Search buildings by address or building name
                </label>
                <Input
                  className="TopBuildingsListContainer__Search"
                  name="top-buildings-search"
                  type="search"
                  placeholder="Search for building"
                  value={search}
                  onChange={(_name, value) => setSearch(String(value).slice(0, SEARCH_MAX_LENGTH))}
                  prefix={<Icon iconName="Search" size={12} />}
                  suffix={
                    search ? (
                      <button
                        type="button"
                        className="TopBuildingsListContainer__SearchClear"
                        aria-label="Clear search"
                        onClick={() => setSearch("")}
                      >
                        <Icon iconName="X" size={12} />
                      </button>
                    ) : undefined
                  }
                />
              </>
            )}
            {storageKey && (
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                aria-label="Configure columns"
                onClick={() => setIsConfigModalOpen(true)}
              />
            )}
            {onToggleFullscreen && (
              <Button
                variant="outline"
                size="sm"
                iconName={isFullscreen ? "Minimize2" : "Maximize2"}
                aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={onToggleFullscreen}
              />
            )}
            {onCollapse && (
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronsDown"
                aria-label="Collapse buildings list"
                onClick={onCollapse}
              />
            )}
          </div>
        </div>
        <DataGrid
          className={`TopBuildingsList ${className || ""}`}
          shouldSuppressBorder
          columns={breakdownColumns}
          columnWidths={columnWidths}
          onColumnWidthsChange={setColumnWidths}
          rows={rows}
          rowKeyGetter={(row) => row.id}
          setParentRef={setGridRef}
          onScroll={loadMore}
          isFetching={isLoading}
          onCellClick={handleCellClick}
          onCellDoubleClick={handleCellDoubleClick}
          selectedRowIds={focusedBuildingIds ?? undefined}
          onSelectedCellChange={handleSelectedCellChange}
          onCellContextMenu={handleCellContextMenu}
        />
        {!isLoading && !hasError && rows.length === 0 && <div role="status">No matching buildings.</div>}
        {hasError && (
          <Flex align="center" gap="2" role="alert">
            Could not load buildings.
            <Button variant="outline" size="sm" onClick={() => onRetry?.()}>
              Retry
            </Button>
          </Flex>
        )}
        {storageKey && (
          <TopBuildingsListConfigureModal
            isOpen={isConfigModalOpen}
            onClose={() => setIsConfigModalOpen(false)}
            items={allColumnDefs}
            isCustomized={isCustomized}
            onSave={handleSaveConfig}
            onReset={handleResetConfig}
          />
        )}
      </div>
    );
  },
);

TopBuildingsList.displayName = "TopBuildingsList";
